import {
  PET_HOSTING_SERVICE_ID,
  PET_SITTING_SERVICE_ID,
  PET_DAY_CARE_SERVICE_ID,
  HOUSE_VISITING_SERVICE_ID,
  DOG_WALKING_SERVICE_ID,
  DOG_GROOMING_SERVICE_ID,
  DOG_TRAINING_SERVICE_ID,
  SERVICE_TYPES_IDS,
  PET_TYPE_IDS,
  DEFAULT_SERVICE_TYPE_ID,
  DEFAULT_WEEK_DAYS_DATA,
  DEFAULT_PET_TYPES_DATA,
  BOOKING_TYPE_RWB,
} from '~/common/constants/app';
import {
  ALLOWED_ABOUT_THE_SITTER_OPTIONS,
  ALLOWED_PETS_KIDS_AT_HOME_OPTIONS,
  ALLOWED_SITTERS_HOME_OPTIONS,
  EMPTY_SERVICE,
  DEFAULT_PRICE_RANGE,
  ALLOWED_SITTER_REVIEWS_OPTIONS,
  SITTER_REVIEWS_ANY_ID,
} from '~/common/constants/search';
import { BOOKING_TYPE_MARKETPLACE } from '~/components/utils/search';

import { getDateFromString } from './utils';
import { RWB_SERVICE_NAMES } from '../SearchFiltersForm/constants';

import type {
  LocationValues,
  PetTypes,
  ServiceFilterDetails,
  SearchFilters,
  SearchResultsArgs,
  WeekDays,
  AdvancedFilters,
} from '~/common/types/search';

const petTypesMapping: { [key: number]: string } = {
  1: 'smallDog',
  2: 'mediumDog',
  3: 'largeDog',
  4: 'giantDog',
  5: 'puppy',
  6: 'cat',
  7: 'bird',
  8: 'creature',
};

const serviceDetailsMapping: { [key: number]: ServiceFilterDetails } = {
  [PET_HOSTING_SERVICE_ID]: {
    type: 'petHosting',
    quantity: 0,
  },
  [PET_SITTING_SERVICE_ID]: {
    type: 'petSitting',
    quantity: 0,
  },
  [PET_DAY_CARE_SERVICE_ID]: {
    type: 'petDayCare',
    quantity: 0,
  },
  [HOUSE_VISITING_SERVICE_ID]: {
    type: 'houseVisiting',
    quantity: 0,
  },
  [DOG_WALKING_SERVICE_ID]: {
    type: 'dogWalking',
    quantity: 0,
  },
  [DOG_GROOMING_SERVICE_ID]: {
    type: 'grooming',
    quantity: 0,
  },
  [DOG_TRAINING_SERVICE_ID]: {
    type: 'training',
    quantity: 0,
  },
};

const getLocationValuesFromSearchQueryParameters = (
  searchQueryParameters: Pick<
    SearchResultsArgs,
    'lat' | 'lng' | 'q' | 'postcode' | 'stateName' | 'suburb'
  >
): LocationValues => {
  const { lat, lng, postcode, q, stateName, suburb } = searchQueryParameters;

  return {
    query: q as string,
    address: {
      lat: lat as number,
      lng: lng as number,
      postcode: postcode as string,
      stateName: stateName as string,
      suburb: suburb as string,
    },
  };
};

// NOTE: exported for testing purposes only
export const getPetTypesFromSearchQueryParameters = (
  petTypesString: string | undefined
): PetTypes => {
  const petTypes: PetTypes = { ...DEFAULT_PET_TYPES_DATA };

  if (!petTypesString) {
    return petTypes;
  }

  petTypesString
    .split(',')
    .map((petTypeId) => parseInt(petTypeId, 10))
    .filter((petTypeId) => PET_TYPE_IDS.includes(petTypeId))
    .map((petTypeId) => petTypesMapping[petTypeId])
    .forEach((petTypeSlug: string) => {
      petTypes[petTypeSlug] = `${Number(petTypes[petTypeSlug]) + 1}`;
    });

  return petTypes;
};

// NOTE: exported for testing purposes only
export const getWeekdaysFromSearchQueryParameters = (
  weekDaysString: string | undefined
): WeekDays => {
  const weekDays = { ...DEFAULT_WEEK_DAYS_DATA };

  if (!weekDaysString) {
    return weekDays;
  }

  const weekDaysKeys = Object.keys(weekDays);
  weekDaysString.split(',').forEach((dayInt, index) => {
    weekDays[weekDaysKeys[index] as keyof WeekDays] = !!Number(dayInt);
  });

  return weekDays;
};

// NOTE: exported for testing purposes only
export const getServiceDetailsFromSearchQueryParameters = (
  serviceTypeId: number | undefined,
  jobsCountDogWalking: number,
  jobsCountHouseVisiting: number,
  setEmptyInsteadOfDefault = false
): ServiceFilterDetails => {
  if (!serviceTypeId || !SERVICE_TYPES_IDS.includes(serviceTypeId)) {
    return setEmptyInsteadOfDefault
      ? EMPTY_SERVICE
      : serviceDetailsMapping[DEFAULT_SERVICE_TYPE_ID];
  }

  const serviceDetails = serviceDetailsMapping[serviceTypeId];

  if (serviceTypeId === DOG_WALKING_SERVICE_ID) {
    serviceDetails.quantity = jobsCountDogWalking;
  }

  if (serviceTypeId === HOUSE_VISITING_SERVICE_ID) {
    serviceDetails.quantity = jobsCountHouseVisiting;
  }

  return serviceDetails;
};

// NOTE: exported for testing purposes only
export const getPriceArray = (price: string | undefined): string[] => {
  if (!price) {
    return DEFAULT_PRICE_RANGE;
  }

  const priceArray: string[] = price
    .split(',')
    // ensure values are strings that could be converted into numbers
    .filter((priceValue) => !isNaN(Number(priceValue)) && priceValue.trim() !== '');

  return priceArray.length === 2 ? priceArray : DEFAULT_PRICE_RANGE;
};

// NOTE: exported for testing purposes only
export const getAllowedOptionsArray = (
  parameters: string | undefined,
  allowedArray: string[]
): string[] => {
  if (!parameters || allowedArray.length === 0) {
    return [];
  }

  const options: string[] = parameters
    .split(',')
    .filter((sitterHomeOption: string) => allowedArray.includes(sitterHomeOption));

  return options;
};

export const getAllowedOption = (
  parameters: string | undefined,
  allowedArray: string[]
): string => {
  if (!parameters || allowedArray.length === 0) {
    return '';
  }

  const option = allowedArray.find((arrayOption: string) => arrayOption === parameters) || '';

  return option;
};

export const getAdvancedFilters = (
  price?: string,
  sitterOptions?: string,
  reviewsMin?: string
): AdvancedFilters => ({
  price: getPriceArray(price),
  sittersHome: getAllowedOptionsArray(sitterOptions, ALLOWED_SITTERS_HOME_OPTIONS),
  sittersPetsKids: getAllowedOptionsArray(sitterOptions, ALLOWED_PETS_KIDS_AT_HOME_OPTIONS),
  aboutSitter: getAllowedOptionsArray(sitterOptions, ALLOWED_ABOUT_THE_SITTER_OPTIONS),
  reviewsMin: getAllowedOption(reviewsMin, ALLOWED_SITTER_REVIEWS_OPTIONS) || SITTER_REVIEWS_ANY_ID,
});

export const mapSearchQueryParametersToSearchFilters = (
  {
    bookingType = BOOKING_TYPE_MARKETPLACE,
    jobsCountDogWalking,
    jobsCountHouseVisiting,
    petTypes,
    startDate,
    endDate,
    scheduledDates,
    serviceTypeId,
    startDateOfWeek = '',
    weekDays,
    lat,
    lng,
    q,
    postcode,
    stateName,
    suburb,
    sitterOptions,
    sort,
    price,
    reviewsMin,
  }: Omit<SearchResultsArgs, 'additionalServiceTypeIds' | 'distinctId' | 'offset' | 'size' | 'url'>,
  setEmptyPropertiesInsteadOfDefault = false
): SearchFilters => ({
  bookingType,
  chronology: {
    endDate: getDateFromString(endDate),
    rwbStartDate: getDateFromString(startDateOfWeek),
    scheduledDates: scheduledDates?.split(',').filter((date) => date) ?? [],
    startDate: getDateFromString(startDate),
    weekDays: getWeekdaysFromSearchQueryParameters(weekDays),
  },
  service: getServiceDetailsFromSearchQueryParameters(
    serviceTypeId,
    jobsCountDogWalking as number,
    jobsCountHouseVisiting as number,
    setEmptyPropertiesInsteadOfDefault
  ),
  location: getLocationValuesFromSearchQueryParameters({
    lat,
    lng,
    q,
    postcode,
    stateName,
    suburb,
  }),
  petTypes: getPetTypesFromSearchQueryParameters(petTypes),
  advancedFilters: getAdvancedFilters(price, sitterOptions, reviewsMin),
  sort,
});

export const getSearchFilterBookingType = (searchFilters: SearchFilters): string => {
  const isDayTimeService = RWB_SERVICE_NAMES.includes(searchFilters.service.type);
  const currentBookingType = searchFilters?.bookingType;

  if (
    isDayTimeService &&
    currentBookingType === BOOKING_TYPE_MARKETPLACE &&
    searchFilters.chronology.scheduledDates &&
    searchFilters.chronology.scheduledDates.length > 0
  ) {
    return currentBookingType;
  }

  return isDayTimeService ? BOOKING_TYPE_RWB : currentBookingType;
};
